import * as turf from "@turf/turf";
import * as _ from "lodash";
import React, { useContext } from "react";
import ReactJSON from "react-json-view";
import { State } from "../context/State";
import { bufferForm } from "../forms/buffer";
import { changeName } from "../forms/changeName";
import { gridForm } from "../forms/gridForm";
import { getIntersectForm } from "../forms/intersect";
import { Emoji } from "../ui/emoji";
import { DynamicForm } from "./DynamicForm";
import Link from "./Link";
import { fitBounds, getName, splitFCIntoFeatures } from "./util";
import { VerticalSpace } from "./VerticalSpace";
import { stringify } from "wellknown";

export function Actions({ layer, setContentKey, handleClose }) {
  const context = useContext(State);
  const { addLayer, metaLookup, setView, view, layers, showLibrary } = context;

  const isPolygon =
    _.get(layer, ["type"]) === "Feature" &&
    _.get(layer, ["geometry", "type"]) === "Polygon";

  const isFeatureCollection = _.get(layer, ["type"]) === "FeatureCollection";

  const addBB = () => {
    let bb = turf.bboxPolygon(turf.bbox(layer));
    bb.properties.name = `Bounding box of ${getName(layer)}`;
    addLayer({ layer: bb });
  };

  const tesselateLayer = layer => {
    const t = turf.tesselate(layer);

    addLayer({ layer: t });
  };

  const SideIcon = ({ show = true, isActive = true, children }) =>
    show ? (
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div>{children}</div>
        <div>
          <Emoji isActive={isActive} name="rightArrow" />
        </div>
      </div>
    ) : (
      <div>{children}</div>
    );

  return (
    <div>
      <Link
        hoverDesc="Move map to layer"
        onClick={() => fitBounds({ layer, setView, view, showLibrary })}
      >
        Go To...
      </Link>
      <VerticalSpace s />
      <Link
        hoverDesc="Create bounding box from layer"
        onClick={addBB.bind(this)}
      >
        Create Bounding Box
      </Link>
      <VerticalSpace s />
      <SideIcon>
        <Link
          hoverDesc="Change layer name"
          onClick={() =>
            setContentKey(
              <DynamicForm
                setContentKey={setContentKey}
                layer={layer}
                handleClose={handleClose}
                form={{ ...changeName, initial: { name: getName(layer) } }}
              />
            )
          }
        >
          Change Name
        </Link>
      </SideIcon>
      <VerticalSpace s />
      <SideIcon>
        <Link
          hoverDesc="Create grid from layer"
          onClick={() =>
            setContentKey(
              <DynamicForm
                setContentKey={setContentKey}
                layer={layer}
                handleClose={handleClose}
                form={gridForm}
              />
            )
          }
        >
          Create Grid
        </Link>
      </SideIcon>
      <VerticalSpace s />
      <SideIcon>
        <Link
          hoverDesc="Create new buffered layer"
          onClick={() =>
            setContentKey(
              <DynamicForm
                setContentKey={setContentKey}
                layer={layer}
                handleClose={handleClose}
                form={bufferForm}
              />
            )
          }
        >
          Create Buffer
        </Link>
      </SideIcon>
      <VerticalSpace s />
      <SideIcon>
        <Link
          hoverDesc="Intersect layer with another layer"
          onClick={() =>
            setContentKey(
              <DynamicForm
                setContentKey={setContentKey}
                layer={layer}
                handleClose={handleClose}
                form={getIntersectForm(layers)}
                layers={layers}
              />
            )
          }
        >
          Intersect
        </Link>
      </SideIcon>
      <VerticalSpace s />
      <SideIcon show={false}>
        <Link
          hoverDesc="Create tesselation layer of feature layer"
          isActive={isPolygon}
          onClick={() => tesselateLayer(layer)}
        >
          Tesselate
        </Link>
      </SideIcon>
      <VerticalSpace s />
      <SideIcon show={false}>
        <Link
          isActive={isFeatureCollection}
          hoverDesc="Split feature collection into separate layers"
          onClick={() => splitFCIntoFeatures(layer, addLayer)}
        >
          Split Into Layers
        </Link>
      </SideIcon>

      {/* Start of Inpsect */}
      <VerticalSpace m />
      <div>Inspect</div>
      <VerticalSpace s />
      <SideIcon>
        <Link
          hoverDesc="View or copy layer GeoJSON representation"
          onClick={() =>
            setContentKey(<ReactJSON enableClipboard={true} src={layer} />)
          }
        >
          View Layer JSON
        </Link>
      </SideIcon>
      <VerticalSpace s />
      {layer._id && (
        <SideIcon>
          <Link
            hoverDesc="View or copy layer meta data"
            onClick={() => setContentKey(jsonViewerMeta())}
          >
            View Meta JSON
          </Link>
        </SideIcon>
      )}
      <VerticalSpace s />
      <SideIcon isActive={isPolygon}>
        <Link
          isActive={isPolygon}
          hoverDesc="View as WKT"
          onClick={() => setContentKey(<div>{stringify(layer)}</div>)}
        >
          View as WKT
        </Link>
      </SideIcon>
    </div>
  );

  function jsonViewerMeta() {
    return (
      <ReactJSON
        enableClipboard={true}
        src={{ _id: layer._id, ...metaLookup[layer._id] }}
      />
    );
  }
}
